import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { StudioPage, Panel, Badge, DataTable, Drawer, DBlock, DKV, WhyButton, Loading, cx, confColor } from "./StudioShared";
import { Sparkles, Cpu, Trophy, GitBranch, Info } from "lucide-react";

const PRIORITIES = [["balanced", "Balanced"], ["quality", "Highest Quality"], ["cost", "Cheapest"], ["speed", "Fastest"]];
const TASKS = [["", "All Tasks"], ["copy", "Website Copy"], ["seo", "SEO"], ["proposal", "Proposal"], ["branding", "Branding"], ["blog", "Blog"], ["faq", "FAQ"], ["color", "Color Palette"], ["sitemap", "Sitemap"], ["cta", "CTA"]];
const DIMS = [["model", "Model"], ["provider", "Provider"], ["strategist", "Strategist"], ["workflow", "Workflow"]];
const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);
const usd = (v) => (v == null ? "—" : `$${Number(v).toFixed(4)}`);
const ms = (v) => (v == null ? "—" : v < 1000 ? `${v}ms` : `${(v / 1000).toFixed(1)}s`);
const MiniMetric = ({ label, value, cls = "text-white" }) => (
  <div className="border border-white/10 rounded-sm px-3 py-2">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={cx("font-mono text-sm mt-0.5", cls)}>{value}</div>
  </div>
);

export default function IntelligenceRouter() {
  const [task, setTask] = useState("");
  const [priority, setPriority] = useState("balanced");
  const [dim, setDim] = useState("model");
  const [rec, setRec] = useState(null);
  const [perf, setPerf] = useState(null);
  const [perfRow, setPerfRow] = useState(null);

  const loadRec = useCallback(async () => {
    setRec(null);
    try { const r = await api.get(`/router/recommend?priority=${priority}${task ? `&task=${task}` : ""}`); setRec(r.data); }
    catch { setRec({ candidates: [] }); }
  }, [task, priority]);
  const loadPerf = useCallback(async () => {
    setPerf(null);
    try { const r = await api.get(`/router/performance?dimension=${dim}${task ? `&task=${task}` : ""}`); setPerf(r.data.rows || []); }
    catch { setPerf([]); }
  }, [dim, task]);
  useEffect(() => { loadRec(); }, [loadRec]);
  useEffect(() => { loadPerf(); }, [loadPerf]);

  return (
    <StudioPage title="AIPOS Intelligence Router"
      question="Which model is actually best — learned from real mission history, not assumptions?"
      testid="intelligence-router">
      <div className="space-y-6">
        {/* Recommendation */}
        <Panel className="p-4" testid="router-recommendation">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2 text-white text-sm font-semibold"><Sparkles size={15} className="text-gold" /> Live Recommendation</div>
            <select value={task} onChange={(e) => setTask(e.target.value)} data-testid="router-task"
              className="bg-navy-800 border border-white/10 px-2.5 py-1.5 text-xs text-white rounded-sm ml-auto focus:border-gold focus:outline-none">
              {TASKS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="router-priority"
              className="bg-navy-800 border border-white/10 px-2.5 py-1.5 text-xs text-white rounded-sm focus:border-gold focus:outline-none">
              {PRIORITIES.map(([k, l]) => <option key={k} value={k}>Optimize: {l}</option>)}
            </select>
          </div>
          {!rec ? <Loading /> : (() => {
            const best = (rec.candidates || []).find((c) => c.model_id === rec.recommended_model_id) || (rec.candidates || [])[0] || {};
            const bs = best.stats || {};
            const w = { balanced: [50, 25, 25], quality: [70, 15, 15], cost: [30, 60, 10], speed: [30, 10, 60] }[rec.priority] || [50, 25, 25];
            return (
              <div className="space-y-4">
                <div className="flex items-center gap-3 border border-gold/30 bg-gold/5 rounded-sm px-4 py-3" data-testid="router-winner">
                  <Trophy size={20} className="text-gold shrink-0" />
                  <div className="min-w-0">
                    <div className="text-white font-semibold">{rec.label || rec.recommended_model_id}</div>
                    <div className="text-[11px] text-slate-400">{rec.reason}</div>
                  </div>
                  <div className="ml-auto flex items-center gap-2 shrink-0">
                    <WhyButton testid="router-why" title="Why this model?" subtitle={rec.label || rec.recommended_model_id}
                      reasons={[rec.reason, best.source === "learned"
                        ? `Chosen from ${best.samples} real missions (learned from telemetry).`
                        : "Chosen from Model Registry priors (cold-start) — will sharpen with usage."].filter(Boolean)}
                      factors={[
                        { label: `Optimization mode`, value: (PRIORITIES.find(([k]) => k === rec.priority) || [])[1] || rec.priority },
                        { label: `Weights (quality / cost / speed)`, value: `${w[0]}% / ${w[1]}% / ${w[2]}%` },
                        { label: "Quality score", value: best.quality != null ? best.quality : "—" },
                        { label: "Cost score", value: best.cost_score != null ? best.cost_score : "—" },
                        { label: "Speed score", value: best.speed_score != null ? best.speed_score : "—" },
                        { label: "Overall score", value: best.score != null ? best.score : "—" },
                        { label: "Evidence (missions)", value: best.samples ?? 0 },
                      ]}
                      note="The router blends registry priors with learned telemetry. A model becomes 'learned' after ≥3 missions for the scope." />
                    <Badge tone={rec.learning_active ? "green" : "slate"}>{rec.learning_active ? "Learned" : "Priors"}</Badge>
                  </div>
                </div>
                {/* Why-it-won metrics (real telemetry; '—' when no data) */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="router-winner-metrics">
                  <MiniMetric label="Estimated Cost" value={usd(bs.avg_cost)} cls="text-gold" />
                  <MiniMetric label="Confidence" value={rec.confidence ? `${rec.confidence} · ${pct(bs.avg_confidence)}` : pct(bs.avg_confidence)} cls={confColor(bs.avg_confidence || 0)} />
                  <MiniMetric label="Approval Rate" value={pct(bs.approval_rate)} cls={confColor(bs.approval_rate || 0)} />
                  <MiniMetric label="Average Speed" value={ms(bs.avg_duration_ms)} />
                </div>
                <div className="space-y-1.5" data-testid="router-candidates">
                  {(rec.candidates || []).map((c) => (
                    <div key={c.model_id} className="flex items-center gap-3 border border-white/10 rounded-sm px-3 py-2" data-testid={`router-cand-${c.model_id}`}>
                      <Cpu size={13} className="text-slate-500" />
                      <span className="text-slate-200 text-sm w-28 truncate">{c.label || c.model_id}</span>
                      <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-gold" style={{ width: `${Math.min(c.score * 100, 100)}%` }} />
                      </div>
                      <span className="text-gold text-xs w-12 text-right">{c.score}</span>
                      <Badge tone={c.source === "learned" ? "green" : "slate"}>{c.source === "learned" ? `${c.samples} runs` : "prior"}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </Panel>

        {/* Performance matrix */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center gap-2 text-white text-sm font-semibold"><GitBranch size={15} className="text-gold" /> Performance Matrix</div>
            <select value={dim} onChange={(e) => setDim(e.target.value)} data-testid="router-dimension"
              className="bg-navy-800 border border-white/10 px-2.5 py-1.5 text-xs text-white rounded-sm ml-auto focus:border-gold focus:outline-none">
              {DIMS.map(([k, l]) => <option key={k} value={k}>By {l}</option>)}
            </select>
          </div>
          {!perf ? <Loading /> : (
            <DataTable testid="router-perf-table"
              empty="No telemetry yet for this dimension. Run AI missions to populate."
              onRowClick={(r) => setPerfRow(r)}
              rowTestid={(r) => `perf-row-${r.key}`}
              columns={[
                { key: "key", label: dim, render: (r) => <span className="text-white">{r.key}</span> },
                { key: "samples", label: "Missions", render: (r) => <Badge tone="slate">{r.samples}</Badge> },
                { key: "success_rate", label: "Success", render: (r) => <span className="text-emerald-400">{pct(r.success_rate)}</span> },
                { key: "approval_rate", label: "Approval", render: (r) => <span className={confColor(r.approval_rate || 0)}>{pct(r.approval_rate)}</span> },
                { key: "edit_rate", label: "Edit Rate", render: (r) => pct(r.edit_rate) },
                { key: "avg_confidence", label: "Confidence", render: (r) => <span className={confColor(r.avg_confidence || 0)}>{pct(r.avg_confidence)}</span> },
                { key: "avg_cost", label: "Avg Cost", render: (r) => <span className="text-gold">{usd(r.avg_cost)}</span> },
                { key: "avg_duration_ms", label: "Avg Time", render: (r) => ms(r.avg_duration_ms) },
                { key: "failure_rate", label: "Failure", render: (r) => <span className={r.failure_rate ? "text-red-400" : "text-slate-500"}>{pct(r.failure_rate)}</span> },
              ]}
              rows={perf}
            />
          )}
        </div>

        {perfRow && (
          <Drawer open={!!perfRow} onClose={() => setPerfRow(null)} testid="perf-drill"
            title={perfRow.key} subtitle={`Historical performance · ${(DIMS.find(([k]) => k === dim) || [])[1] || dim}`}>
            <DBlock title="Ranking metrics">
              <div className="space-y-1.5">
                <DKV label="Missions (evidence)" value={perfRow.samples ?? 0} />
                <DKV label="Success Rate" value={pct(perfRow.success_rate)} />
                <DKV label="Approval Rate" value={pct(perfRow.approval_rate)} />
                <DKV label="Confidence" value={pct(perfRow.avg_confidence)} />
                <DKV label="Cost" value={usd(perfRow.avg_cost)} />
                <DKV label="Average Time" value={ms(perfRow.avg_duration_ms)} />
                <DKV label="Edit Rate (revisions proxy)" value={pct(perfRow.edit_rate)} />
                <DKV label="Retry Rate" value={pct(perfRow.retry_rate)} />
                <DKV label="Failure Rate" value={pct(perfRow.failure_rate)} />
              </div>
            </DBlock>
            <p className="text-[11px] text-slate-500">Aggregated from real mission telemetry for this {dim}. Metrics show "—" until enough missions accumulate.</p>
          </Drawer>
        )}

        <div className="flex items-start gap-2 text-[11px] text-slate-500 border border-white/10 rounded-sm px-3 py-2" data-testid="router-note">
          <Info size={13} className="mt-0.5 shrink-0" />
          <span>The router blends registry priors with learned telemetry. A model becomes "learned" after ≥3 missions for the scope. When you generate with <b>Auto (AIPOS Recommended)</b>, this engine picks the model — and improves as your mission history grows.</span>
        </div>
      </div>
    </StudioPage>
  );
}
